"use client";

import { memo } from "react";
import {
  Trophy,
  Users,
  Calendar,
  DollarSign,
  Building2,
  Globe,
  ShieldCheck,
} from "lucide-react";
import { useTranslations } from "next-intl";

const formatRegion = (region: string): string => {
  return region.replace(/(\d+)$/, "").toUpperCase() || region;
};

import { Link } from "@/i18n/navigation";
import { Badge } from "@/shared/ui/data-display/badge";
import { TournamentStatusBadge } from "@/entities/tournament/ui/tournament-status-badge";
import { cn } from "@/shared/lib/utils";
import { getBannerUrl } from "@/shared/lib/image-utils";
import { useCountdown } from "@/shared/lib/use-countdown";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";
import { formatMinRequiredRankRu } from "@/shared/lib/riot-rank";
import { Button } from "@/shared/ui/buttons/button";
import { TempTeamCreateDialog } from "@/features/tournament/temp-team-create-dialog";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { CardBanner } from "@/shared/ui/card-banner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Progress } from "@/shared/ui/progress";

import { Tournament } from "@/entities/tournament/model/types";
import {
  getTournamentDisplayPrizeLabel,
  summarizePrizeDistribution,
} from "@/entities/tournament/lib/prize-display";
import { useOrganizationRestriction } from "@/hooks/use-organization-restriction";
import { useUserProfile } from "@/hooks/useAuth";
import { FriendsInTournamentBadge } from "@/entities/tournament/ui/friends-in-tournament-badge";
import type { FriendListItem } from "@/shared/api/friends";
import { AlertTriangle } from "lucide-react";

const StatusBadge = ({
  tournament,
  className,
}: {
  tournament: Tournament;
  className?: string;
}) => {
  const t = useTranslations("Tournaments");
  if (
    (tournament.status === "completed" || tournament.status === "Completed") &&
    tournament.winner
  ) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 rounded-md bg-background/70 dark:bg-black/50 px-2 py-1 backdrop-blur-sm shadow-sm",
          className,
        )}
      >
        <span className="text-xs text-muted-foreground">{t("card.winner")}</span>
        <Avatar className="h-5 w-5">
          <AvatarImage
            src={tournament.winner.avatar}
            alt={tournament.winner.name}
          />
          <AvatarFallback>{tournament.winner.name.charAt(0)}</AvatarFallback>
        </Avatar>
        <span className="text-xs font-semibold">{tournament.winner.name}</span>
      </div>
    );
  }
  return (
    <TournamentStatusBadge tournament={tournament} className={className} />
  );
};

interface TournamentCardProps {
  tournament: Tournament;
  friendsInTournament?: { friends: FriendListItem[]; count: number };
  /** Первая карточка списка — LCP страницы (KAN-977). */
  priority?: boolean;
}

export const TournamentCard = memo(({ tournament, friendsInTournament, priority = false }: TournamentCardProps) => {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Tournaments");
  const { data: me } = useUserProfile();
  const countdown = useCountdown(
    tournament?.startDate || new Date().toISOString(),
  );

  // KAN-1194: хук до раннего выхода — порядок хуков не должен зависеть от данных.
  const { isRestricted } = useOrganizationRestriction(
    tournament?.organizationId ?? undefined,
  );

  if (!tournament) {
    return null;
  }

  const requiredTeamSize = tournament.maxTeamSize ?? 5;
  const isClassic = tournament.format === "Classic";
  const isTempTeamTournament =
    isClassic && requiredTeamSize > 1 && requiredTeamSize < 5;
  // Arena Random — соло-рега (как Draft), команда не нужна. Командная форма
  // на /register для неё не подходит (требует выбор команды + Riot), поэтому
  // ведём на детальную страницу, где работает one-click соло-регистрация.
  const isArena =
    tournament.format === "Arena" ||
    (tournament as { type?: string }).type === "Arena";
  const isArenaRandom =
    isArena &&
    (tournament as { arenaTeamMode?: string }).arenaTeamMode === "Random";
  const canRegister =
    !isRestricted &&
    (tournament.capabilities?.canRegister ??
    tournament.status === "RegistrationOpen");

  const organizerHref = tournament.organizer
    ? `/organizations/${tournament.organizer.url ?? tournament.organizer.id}`
    : undefined;

  const minRequiredRankRu = formatMinRequiredRankRu(
    tournament.minRequiredRank,
  );

  // Бейдж формата. У Arena нет понятия сетки (bracketType в БД заполнен, но
  // бессмысленен) — показываем режим Арены, а не «Single Elimination».
  const bracketLabel = (() => {
    if (isArena) {
      const mode = (tournament as { arenaTeamMode?: string }).arenaTeamMode;
      return mode === "Random" ? tUi("litTournamentCard.arenaSluchaynyeKomandy") : tUi("litTournamentCard.arena");
    }
    const bt = String(tournament.bracketType ?? "").toLowerCase();
    if (bt.includes("double")) return "Double Elimination";
    if (bt.includes("single")) return "Single Elimination";
    if (bt.includes("swiss")) return "Swiss";
    if (bt.includes("round") || bt.includes("robin")) return "Round Robin";
    return null;
  })();

  return (
    <Card className="relative gap-0 py-0 p-0 overflow-hidden hover:shadow-lg transition-shadow flex flex-col group h-full">
      {/* KAN-671: карточка турнира открывает турнир — это и было ожиданием, а клик
          попадал на организатора, потому что его блок был ссылкой во всю ширину, а
          сама карточка ссылкой не была вовсе.

          Оверлей, а не обёртка <Link> вокруг всего: внутри уже есть ссылки на
          организатора и кнопки, и вложенные <a> дали бы невалидную разметку и
          сломанную клавиатурную навигацию. Оверлей лежит под интерактивом (z-[1]
          против z-[2]), поэтому вложенные ссылки продолжают работать сами. */}
      <Link
        href={`/tournaments/${tournament.id}`}
        aria-label={tournament.name}
        className="absolute inset-0 z-[1] rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <CardBanner
        src={getBannerUrl(tournament.image)}
        alt={tournament.name}
        className="bg-muted"
        overlayClassName="from-black/10"
        brandFallback
        priority={priority}
        // GIF-баннеры может создать только org goodluck (аплоуд гейтится), поэтому
        // анимацию можно включать глобально: у остальных баннеры jpg/png — флаг no-op.
        allowAnimatedGif
      >
        <div className="absolute top-2 right-2 z-20">
          <StatusBadge tournament={tournament} />
        </div>
      </CardBanner>

      <CardContent className="flex-1 min-h-0 flex flex-col gap-3 sm:gap-4 pb-3 sm:pb-4 pt-3 sm:pt-4 px-3 sm:px-4">
        <div className="space-y-2">
            <h3 className="text-base sm:text-lg font-bold leading-tight text-foreground line-clamp-2 min-h-[1.5em]">
                {tournament.name}
            </h3>
            <div className="flex flex-wrap gap-2 items-center">
              {bracketLabel && (
                <Badge variant="outline" className="bg-background/60">
                  {bracketLabel}
                </Badge>
              )}
              <FriendsInTournamentBadge
                tournamentId={String(tournament.id ?? "")}
                friends={friendsInTournament?.friends}
                count={friendsInTournament?.count}
                isAuthenticated={!!me}
              />
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5em]">
                {tournament.description || t("card.noDescription")}
            </p>
        </div>
        
        {/* Tournament Info - Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 text-sm">
          <div className="flex items-start gap-2">
            <Users className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
            <div className="w-full min-w-0">
              {tournament.type === "Draft" ? (
                // Драфт: участники набираются в пул (без лимита команд), из них
                // капитаны собирают N команд — показываем раздельно.
                <>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{t("card.participants")}</span>
                    <span className="font-medium text-foreground">
                      {tournament.registeredPlayersCount ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{tUi("tournamentCard.komand")}</span>
                    <span className="font-medium text-foreground">
                      {tournament.maxTeamsInDraft ?? "—"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{t("card.participants")}</span>
                    <span className="font-medium text-foreground">
                      {tournament.currentTeams}/{tournament.maxTeams}
                    </span>
                  </div>
                  <Progress
                    aria-label={t("card.participants")}
                    value={
                      (tournament.maxTeams ?? 0) > 0
                        ? ((tournament.currentTeams ?? 0) /
                            (tournament.maxTeams ?? 1)) *
                          100
                        : 0
                    }
                    className="h-1.5 mt-1"
                  />
                </>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2">
            <DollarSign className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{t("card.prizePool")}</div>
              {(() => {
                const label = getTournamentDisplayPrizeLabel(
                  tournament.prizePool,
                  tournament.prizeDistribution,
                );
                if (label.type === "prizes") {
                  const compact = [label.prizeTypeLabel, label.placesRange].filter(Boolean).join(", ");
                  return (
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground text-sm leading-snug truncate" title={label.summary}>
                        {compact}
                      </p>
                      {label.summary && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug wrap-break-word">
                          {label.summary}
                        </p>
                      )}
                    </div>
                  );
                }
                return (
                  <>
                    <span className="font-semibold text-foreground truncate block">
                      {tournament.currency === "USD" ? "$" : "₽"}
                      {label.value.toLocaleString()}
                    </span>
                    {(tournament.prizeDistribution?.length ?? 0) > 0 && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug">
                        {summarizePrizeDistribution(tournament.prizeDistribution)}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Calendar className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{t("card.startDate")}</div>
              <span className="font-medium text-foreground truncate block">
                {tournament.startDate
                  ? formatDateOnlyMsk(tournament.startDate)
                  : "N/A"}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Trophy className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{t("card.format")}</div>
              <span className="font-medium text-foreground truncate block">{tournament.format}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 text-sm">
          {tournament.region && (
            <div className="flex items-start gap-2">
              <Globe className="h-4 w-4 text-cyan-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">{t("card.region")}</div>
                <span className="font-medium text-foreground truncate block">
                  {formatRegion(tournament.region)}
                </span>
              </div>
            </div>
          )}
          {minRequiredRankRu && (
            <div className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-teal-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">{t("card.minRank")}</div>
                <span className="font-medium text-foreground truncate block">
                  {minRequiredRankRu}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Organization Info */}
        {tournament.organizer && organizerHref && (
          <div className="pt-3 border-t mt-auto">
            {/* KAN-671: к организатору ведёт аватар и имя, а не полоса во всю ширину —
                w-fit, иначе ссылка снова перехватывает клик по пустому месту строки. */}
            <Link
                href={organizerHref}
                className="relative z-[2] inline-flex w-fit max-w-full items-center gap-2 group/org hover:opacity-80 transition-opacity"
            >
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage
                  src={tournament.organizer.avatar ?? undefined}
                  alt={tournament.organizer.name}
                />
                <AvatarFallback className="text-xs">
                  <Building2 className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium text-muted-foreground group-hover/org:text-primary transition-colors truncate">
                {tournament.organizer.name}
              </span>
            </Link>
          </div>
        )}

        {/* Actions — время над кнопками, кнопки в колонку: Участвовать → Подробнее */}
        {/* z-[2]: кнопки выше оверлей-ссылки карточки (KAN-671).
            mt-auto: блок прижат к низу карточки. Содержимое выше разной высоты —
            у кого-то бейдж формата, у кого-то лишняя строка «Команд», у кого-то
            блок организатора, — и без этого «Подробнее» гуляла по вертикали от
            карточки к карточке, хотя сами карточки в ряду одной высоты. */}
        <div className="relative z-[2] mt-auto pt-2 flex flex-col gap-2">
          {isRestricted && (
            <div className="p-2 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-md flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
              <p className="text-xs text-orange-800 dark:text-orange-200">
                {t("card.restricted")}
              </p>
            </div>
          )}
          {canRegister && !countdown.isFinished && (
            <p className="text-center text-[10px] text-muted-foreground truncate">
              {t("card.closesIn")} <span className="font-medium">{countdown.days}д {countdown.hours}ч {countdown.minutes}м</span>
            </p>
          )}
          <div className="flex flex-col gap-2">
            {canRegister &&
              (isTempTeamTournament ? (
                <TempTeamCreateDialog
                  tournamentId={String(tournament.id ?? "")}
                  goToRegisterAfterCreate
                  trigger={
                    <Button className="w-full h-9" variant="secondary">
                      {t("card.participate")}
                    </Button>
                  }
                />
              ) : isArenaRandom ? (
                <Button asChild className="w-full h-9" variant="secondary">
                  {/* slug нет в контракте бэка (mapTournament его не пробрасывает) — ссылки по id */}
                  <Link href={`/tournaments/${tournament.id}`}>
                    {t("card.participate")}
                  </Link>
                </Button>
              ) : (
                <Button asChild className="w-full h-9" variant="secondary">
                  <Link href={`/tournaments/${tournament.id}/register`}>
                    {t("card.participate")}
                  </Link>
                </Button>
              ))}
            {isRestricted && (
              <Button className="w-full h-9" variant="secondary" disabled>
                {t("card.participate")}
              </Button>
            )}
            <Button asChild className="w-full h-9" variant="default">
              <Link href={`/tournaments/${tournament.id}`}>{t("card.details")}</Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

TournamentCard.displayName = "TournamentCard";
